import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";
import { apiGet } from "./api";
import type { PortalConfig } from "./config";
import { date } from "./format";

interface Hit { ticketNo: number; firstName: string | null; lastName: string | null; email: string; status: string; createdAt: string }

export const MIN_CHARS = 3;

/** Master search in the header: any ticket by number, customer name or email (3+ characters). */
export function HeaderSearch({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const token = auth.user?.access_token;
  const [text, setText] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const q = text.trim().replace(/^#/, "");

  // Wait for a pause in typing, then search; a newer search cancels the older one.
  useEffect(() => {
    if (!token || q.length < MIN_CHARS) { setHits([]); setLoading(false); return; }
    const ctrl = new AbortController();
    setLoading(true);
    const t = window.setTimeout(() => {
      apiGet<{ hits: Hit[] }>(config, token, `/search?q=${encodeURIComponent(q)}`, ctrl.signal)
        .then((r) => { setHits(r.hits); setActive(r.hits.length ? 0 : -1); setLoading(false); })
        .catch(() => { if (!ctrl.signal.aborted) setLoading(false); });
    }, 250);
    return () => { window.clearTimeout(t); ctrl.abort(); };
  }, [q, token, config]);

  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (ticketNo: number) => {
    setOpen(false);
    setText("");
    navigate(`/tickets/${ticketNo}`);
  };

  const submit = () => {
    if (active >= 0 && hits[active]) return go(hits[active].ticketNo);
    // A full ticket number typed before results arrive still works.
    if (/^\d{4,9}$/.test(q)) return go(Number(q));
  };

  const showList = open && q.length >= MIN_CHARS;

  return (
    <div className="header-search" ref={box}>
      <form role="search" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <input
          type="search"
          value={text}
          placeholder="Ticket # or customer"
          aria-label="Search all tickets by number, name or email"
          aria-expanded={showList}
          aria-controls="header-search-results"
          aria-autocomplete="list"
          role="combobox"
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); setOpen(true); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            else if (e.key === "Escape") setOpen(false);
          }}
        />
      </form>
      {showList && (
        <ul id="header-search-results" className="search-results" role="listbox">
          {hits.map((h, i) => (
            <li
              key={h.ticketNo}
              role="option"
              aria-selected={i === active}
              className={i === active ? "active" : ""}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); go(h.ticketNo); }}
            >
              <span className="hit-name">{[h.firstName, h.lastName].filter(Boolean).join(" ") || h.email}</span>
              <span className="hit-no">#{h.ticketNo}</span>
              <span className="hit-sub">{h.status} · {date(h.createdAt)}</span>
            </li>
          ))}
          {!hits.length && <li className="hit-empty">{loading ? "Searching…" : "No tickets found."}</li>}
        </ul>
      )}
    </div>
  );
}
